import {
  Grid,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Slider,
  Box,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  YouTubeTouchpointSetup,
  YouTubeTouchpointStateKeys,
  YTTargetGroup,
} from 'pages/calculator/MediaPlanning/components/Touchpoints/interfaces';
import usePlanStore from '../../../../../../../store/planStore';
import convertDecimalToPercentValue from '../../../../Plan/Countries/utils/convertDecimalToPercentValue';
import GoldenRuleBox from '../../MetaTouchpoint/GoldenRuleBox/GoldenRuleBox';
import { ObjectSelect } from '../../../../common/ObjectSelect/ObjectSelect';
import renderSaturation from '../../TikTokTouchpoint/TargetGroupSelectors/utils/renderSaturation';
import getSaturationNullish from '../../utils/getSaturationNullish';
import getSaturationMinEqualToMax from '../../utils/getSaturationMinEqualToMax';
import getGenericMinEqualToMax from './utils/getGenericMinEqualToMax';
import linksConfig from '../../../../../../../config/linking';
import HelperTooltip from '../../../../../../../shared/HelperTooltip/HelperTooltip';

interface TargetGroupSelectorsProps {
  minMaxSelectorsValues?: YouTubeTouchpointSetup;
  targetGroupSaturation?: number | null;
  skipCpvValue?: number | null;
  unSkipValue?: number | null;
  bumperValue?: number | null;
  handleChangeStateValues: (
    key: YouTubeTouchpointStateKeys,
    value: number | string | object
  ) => void;
}

function TargetGroupSelectors({
  targetGroupSaturation,
  skipCpvValue,
  unSkipValue,
  bumperValue,
  handleChangeStateValues,
  minMaxSelectorsValues,
}: TargetGroupSelectorsProps) {
  const { t } = useTranslation();

  const { countryForms, selectedCountry } = usePlanStore();

  const skipSliderMin = 0;
  const skipSliderMax = 1;

  const { youTubeConfig } = countryForms[selectedCountry!];
  const isTargetGroupsError =
    youTubeConfig?.error && !youTubeConfig?.targetGroup;

  const countryForm = countryForms[selectedCountry!];

  return (
    <>
      <Grid container>
        <Grid item xs={3} sx={{ pr: '24px' }}>
          <ObjectSelect<YTTargetGroup>
            value={
              youTubeConfig?.targetGroup
                ? {
                    value: youTubeConfig.targetGroup,
                    name:
                      youTubeConfig.targetGroup.targetGroup ===
                      minMaxSelectorsValues?.recommendedTargetGroup
                        ? `${youTubeConfig.targetGroup.targetGroup} (recommended)`
                        : youTubeConfig.targetGroup.targetGroup ?? undefined,
                  }
                : null
            }
            onChange={(item) =>
              handleChangeStateValues('targetGroup', item.value)
            }
            items={
              countryForms[selectedCountry!].youTubeConfig?.targetGroups?.map(
                (group) => ({
                  value: group,
                  name:
                    group.targetGroup ===
                    minMaxSelectorsValues?.recommendedTargetGroup
                      ? `${group.targetGroup} (recommended)`
                      : group.targetGroup,
                })
              ) || []
            }
            getOptionKey={(item) => item.targetGroup}
            selectName={t('mediaPlanning.targetGroup')}
            labelId="target-group"
            error={isTargetGroupsError}
            errorMessage={t('mediaPlaning.errors.targetGroup')}
          />
        </Grid>
        <Grid
          item
          xs={3}
          sx={{ pr: '24px', display: 'flex', flexDirection: 'column' }}
        >
          <Typography variant="caption" lineHeight={1}>
            {renderSaturation(
              t('mediaPlanning.targetGroupSaturation'),
              targetGroupSaturation ?? 0,
              {
                min: minMaxSelectorsValues?.saturationMin,
                max: minMaxSelectorsValues?.saturationMax,
                default: minMaxSelectorsValues?.saturationDefault,
              }
            )}
          </Typography>
          <Slider
            valueLabelDisplay="auto"
            step={0.05}
            value={targetGroupSaturation ?? 0}
            min={minMaxSelectorsValues?.saturationMin ?? 0}
            max={minMaxSelectorsValues?.saturationMax ?? 1}
            slots={{
              track: getSaturationMinEqualToMax(minMaxSelectorsValues)
                ? () => null
                : undefined,
            }}
            marks={
              minMaxSelectorsValues?.saturationMin !== null &&
              minMaxSelectorsValues?.saturationMax !== null
                ? [
                    {
                      value: minMaxSelectorsValues?.saturationMin ?? 0,
                      label: convertDecimalToPercentValue(
                        minMaxSelectorsValues?.saturationMin
                      ),
                    },
                    ...(minMaxSelectorsValues?.saturationMin !==
                    minMaxSelectorsValues?.saturationMax
                      ? [
                          {
                            value: minMaxSelectorsValues?.saturationMax ?? 1,
                            label: convertDecimalToPercentValue(
                              minMaxSelectorsValues?.saturationMax
                            ),
                          },
                        ]
                      : []),
                  ]
                : []
            }
            valueLabelFormat={(value) =>
              `${convertDecimalToPercentValue(value).toFixed(0)}%`
            }
            onChange={(_, newValue) =>
              handleChangeStateValues('targetGroupSaturation', +newValue)
            }
            disabled={getSaturationMinEqualToMax(minMaxSelectorsValues)}
          />
        </Grid>
        <Grid item xs={6} display="flex">
          {(countryForm?.youTubeConfig?.targetGroup?.targetSizePopulation ??
            0) *
            (countryForm?.youTubeConfig?.saturation ?? 0) <
            (countryForm.youTubeConfig?.minReach ?? 0) && (
            <GoldenRuleBox
              text="mediaPlanning.errors.goldenRuleYT"
              colSpan={9}
            />
          )}
          <HelperTooltip
            tooltipData={linksConfig.seeGoldenRules}
            sx={{ marginLeft: '12px', alignSelf: 'flex-start' }}
          />
        </Grid>
      </Grid>
      <Grid container>
        <Grid item xs={6} sx={{ pr: '24px' }}>
          <Box
            sx={{
              p: '8px 24px 18px 24px',
              borderRadius: '5px',
              backgroundColor: 'gray.brightGray',
              rowGap: '24px',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {process.env.REACT_APP_API_VERSION !== 'v3' && (
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography
                  variant="caption"
                  lineHeight={1}
                  textTransform="uppercase"
                >
                  {`${t('mediaPlanning.skip')} (${convertDecimalToPercentValue(
                    skipCpvValue ?? 0
                  )}%)`}
                </Typography>
                <Slider
                  valueLabelDisplay="auto"
                  value={skipCpvValue ?? 0}
                  step={0.05}
                  min={skipSliderMin}
                  max={skipSliderMax}
                  marks={[
                    {
                      value: skipSliderMin,
                      label: convertDecimalToPercentValue(skipSliderMin),
                    },
                    {
                      value: skipSliderMax,
                      label: convertDecimalToPercentValue(skipSliderMax),
                    },
                  ]}
                  valueLabelFormat={(value) =>
                    `${convertDecimalToPercentValue(value).toFixed(0)}%`
                  }
                  onChange={(_, newValue) =>
                    handleChangeStateValues('skipCpvValue', +newValue)
                  }
                  disabled
                />
              </Box>
            )}
            {process.env.REACT_APP_API_VERSION !== 'v3' && (
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography
                  variant="caption"
                  lineHeight={1}
                  textTransform="uppercase"
                >
                  {`${t(
                    'mediaPlanning.unSkip'
                  )} (${convertDecimalToPercentValue(unSkipValue ?? 0)}%)`}
                </Typography>
                <Slider
                  valueLabelDisplay="auto"
                  value={unSkipValue ?? 0}
                  step={0.05}
                  min={minMaxSelectorsValues?.unskipMin ?? 0}
                  max={minMaxSelectorsValues?.unskipMax ?? 1}
                  marks={[
                    {
                      value: minMaxSelectorsValues?.unskipMin ?? 0,
                      label: convertDecimalToPercentValue(
                        minMaxSelectorsValues?.unskipMin
                      ),
                    },
                    {
                      value: minMaxSelectorsValues?.unskipMax ?? 1,
                      label: convertDecimalToPercentValue(
                        minMaxSelectorsValues?.unskipMax
                      ),
                    },
                  ]}
                  valueLabelFormat={(value) =>
                    `${convertDecimalToPercentValue(value).toFixed(0)}%`
                  }
                  onChange={(_, newValue) =>
                    handleChangeStateValues('unSkipValue', +newValue)
                  }
                  disabled={getGenericMinEqualToMax(
                    minMaxSelectorsValues?.unskipMin,
                    minMaxSelectorsValues?.unskipMax
                  )}
                />
              </Box>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography
                variant="caption"
                lineHeight={1}
                textTransform="uppercase"
              >
                {`${t('mediaPlanning.bumper')} (${convertDecimalToPercentValue(
                  bumperValue ?? 0
                )}%)`}
              </Typography>
              <Slider
                valueLabelDisplay="auto"
                value={bumperValue ?? 0}
                step={0.05}
                min={minMaxSelectorsValues?.bumperMin ?? 0}
                max={minMaxSelectorsValues?.bumperMax ?? 1}
                marks={[
                  {
                    value: minMaxSelectorsValues?.bumperMin ?? 0,
                    label: convertDecimalToPercentValue(
                      minMaxSelectorsValues?.bumperMin
                    ),
                  },
                  {
                    value: minMaxSelectorsValues?.bumperMax ?? 1,
                    label: convertDecimalToPercentValue(
                      minMaxSelectorsValues?.bumperMax
                    ),
                  },
                ]}
                valueLabelFormat={(value) =>
                  `${convertDecimalToPercentValue(value).toFixed(0)}%`
                }
                onChange={(_, newValue) =>
                  handleChangeStateValues('bumperValue', +newValue)
                }
                disabled={getGenericMinEqualToMax(
                  minMaxSelectorsValues?.bumperMin,
                  minMaxSelectorsValues?.bumperMax
                )}
              />
            </Box>
          </Box>
        </Grid>
      </Grid>
    </>
  );
}

export default TargetGroupSelectors;
